import type { ReactNode } from 'react'
import { numberFormat } from 'shared/lib'
import { type ICardFilter } from 'shared/ui'
import type { ITooltipPlacementProps } from 'shared/ui/Tooltip/ui/Tooltip'
import { Base } from 'models'
import type { IResponseTrigger } from 'entities/Trigger/api/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IContactsStatisticFilter } from 'entities/ContactStatistic'

type TriggerFilterConfigKey = Exclude<
  IContactsStatisticFilter,
  'subscribed' | 'skipped' | 'no_replies'
>

interface IFilterConfig {
  title?: string
  tooltipLabel?: ReactNode
  tooltipPlacement?: ITooltipPlacementProps['placement']
}

interface IFilterConfigWithTitle extends IFilterConfig {
  title: string
}

export class TriggerStatistic extends Base {
  is_track_conversion?: boolean
  total_count: number | null = 0
  in_progress_count: number | null = null
  quiet_hours_count: number | null = 0
  success_count: number | null = null
  reply_count: number | null = null
  clicks_count: number | null = null
  conversions_count: number | null
  revenue_cost: string | number | null
  undelivered_count: number | null = null
  unsubscribed_count: number | null = null

  constructor(
    { all_statistics: statistic, short_urls, analytics, is_track_conversion }: IResponseTrigger,
    private _entities?: IEntitiesStore
  ) {
    super()
    this.is_track_conversion = is_track_conversion
    this.total_count = statistic.total_count || null
    this.in_progress_count = statistic.in_progress_count || null
    this.quiet_hours_count = statistic.quiet_hours_count
    this.success_count = statistic.success_count || null
    this.reply_count = statistic.reply_count || null
    this.undelivered_count = statistic.failed_count || null
    this.unsubscribed_count = statistic.opt_out_count || null

    this.clicks_count = short_urls?.length
      ? short_urls.reduce((acc, cur) => acc + cur.clicks, 0)
      : null

    if (analytics?.conversion_value) {
      this.revenue_cost = numberFormat({
        value: analytics?.conversion_value,
        minimumFractionDigits: 0,
        currency: 'USD',
      })
    } else {
      this.revenue_cost = is_track_conversion
        ? numberFormat({
            value: 0,
            minimumFractionDigits: 0,
            currency: 'USD',
          })
        : null
    }
    this.conversions_count = analytics?.users_with_conversion || (is_track_conversion ? 0 : null)
  }

  get in_progress_percent() {
    if (this.in_progress_count && this.total_count) {
      return Math.round((this.in_progress_count / this.total_count) * 100)
    }
    return 0
  }
  get quiet_hours_percent() {
    if (this.quiet_hours_count && this.total_count) {
      return Math.round((this.quiet_hours_count / this.total_count) * 100)
    }
    return 0
  }
  get success_percent() {
    if (this.success_count && this.total_count) {
      return Math.round((this.success_count / this.total_count) * 100)
    }
    return 0
  }
  get reply_percent() {
    if (this.reply_count && this.total_count) {
      return Math.round((this.reply_count / this.total_count) * 100)
    }
    return 0
  }
  get clicks_percent() {
    if (this.clicks_count && this.total_count) {
      return Math.round((this.clicks_count / this.total_count) * 100)
    }
    return 0
  }
  get conversions_percent() {
    if (this.conversions_count && this.total_count) {
      return Math.round((this.conversions_count / this.total_count) * 100)
    }
    return 0
  }
  get undelivered_percent() {
    if (this.undelivered_count && this.total_count) {
      return Math.round((this.undelivered_count / this.total_count) * 100)
    }
    return 0
  }
  get unsubscribed_percent() {
    if (this.unsubscribed_count && this.total_count) {
      return Math.round((this.unsubscribed_count / this.total_count) * 100)
    }
    return 0
  }

  public getFilters = ({
    customConfigs,
    entities,
  }: {
    customConfigs?: Partial<Record<TriggerFilterConfigKey, IFilterConfig>>
    entities?: IEntitiesStore
  }): ICardFilter<IContactsStatisticFilter>[] => {
    const defaultConfigs: Record<TriggerFilterConfigKey, IFilterConfigWithTitle> = {
      sent_to: {
        title: 'Total',
        tooltipLabel: 'Trigger contacts',
      },
      in_progress: {
        title: 'In progress',
        tooltipLabel: ['Contacts who queued', <br key={'br'} />, 'to receive the message'],
      },
      quiet_hours: {
        title: 'Quiet hours',
        tooltipLabel: ['Contacts whose messages', <br key={'br'} />, 'are delayed by quiet hours'],
      },
      success: {
        title: 'Delivered',
        tooltipLabel: ['Contacts who received', <br key={'br'} />, 'the message'],
      },
      replied: {
        title: 'Replies',
        tooltipLabel: ['Contacts who replied', <br key={'br'} />, 'to the message'],
      },
      clicked: {
        title: 'Clicks',
        tooltipLabel: ['Contacts who clicked', <br key={'br'} />, 'on the link(s)'],
      },
      conversions: {
        title: 'Conversions',
        tooltipLabel: 'Contacts who converted',
      },
      revenue: {
        title: 'Revenue',
        tooltipLabel: ['Revenue generated by the', <br key={'br'} />, 'contacts who converted'],
      },
      failed: {
        title: 'Undelivered',
        tooltipLabel: ['Contacts who didnn’t', <br key={'br'} />, 'received the message'],
      },
      opted_out: {
        title: 'Unsubscribed',
        tooltipLabel: 'Contacts who opted-out',
      },
    }

    const configKeys = Object.keys(defaultConfigs) as TriggerFilterConfigKey[]
    const configs = configKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          ...defaultConfigs[key],
          ...(customConfigs?.[key] || {}),
        },
      }),
      { ...defaultConfigs }
    )

    const quietHoursFilter: ICardFilter<IContactsStatisticFilter> | null = entities
      ?.organizationStore.quietHoursSettings?.is_active
      ? {
          key: 'quiet_hours',
          title: configs.quiet_hours.title,
          amount: this.quiet_hours_count ?? 0,
          percentage: this.quiet_hours_percent,
          tooltipProps: {
            label: configs.quiet_hours.tooltipLabel,
            placement: configs.quiet_hours.tooltipPlacement || 'top',
          },
        }
      : null

    const filters: ICardFilter<IContactsStatisticFilter>[] = [
      {
        key: 'sent_to',
        title: configs.sent_to.title,
        amount: this.total_count,
        tooltipProps: {
          label: configs.sent_to.tooltipLabel,
          placement: configs.sent_to.tooltipPlacement || 'top',
        },
      },
      {
        key: 'in_progress',
        title: configs.in_progress.title,
        amount: this.in_progress_count,
        percentage: this.in_progress_percent,
        tooltipProps: {
          label: configs.in_progress.tooltipLabel,
          placement: configs.in_progress.tooltipPlacement || 'top',
        },
      },
      ...(quietHoursFilter ? [quietHoursFilter] : []),
      {
        key: 'success',
        title: configs.success.title,
        amount: this.success_count,
        percentage: this.success_percent,
        tooltipProps: {
          label: configs.success.tooltipLabel,
          placement: configs.success.tooltipPlacement || 'top',
        },
      },
      {
        key: 'replied',
        title: configs.replied.title,
        amount: this.reply_count,
        percentage: this.reply_percent,
        tooltipProps: {
          label: configs.replied.tooltipLabel,
          placement: configs.replied.tooltipPlacement || 'top',
        },
      },
      {
        key: 'clicked',
        title: configs.clicked.title,
        amount: this.clicks_count,
        percentage: this.clicks_percent,
        tooltipProps: {
          label: configs.clicked.tooltipLabel,
          placement: configs.clicked.tooltipPlacement || 'top',
        },
      },
      {
        key: 'conversions',
        title: configs.conversions.title,
        amount: this.conversions_count,
        percentage: this.conversions_percent,
        tooltipProps: {
          label: configs.conversions.tooltipLabel,
          placement: configs.conversions.tooltipPlacement || 'top',
        },
      },
      {
        key: 'revenue',
        title: configs.revenue.title,
        amountContent: this.revenue_cost,
        tooltipProps: {
          label: configs.revenue.tooltipLabel,
          placement: configs.revenue.tooltipPlacement || 'top',
        },
      },
      {
        key: 'failed',
        title: configs.failed.title,
        amount: this.undelivered_count,
        percentage: this.undelivered_percent,
        tooltipProps: {
          label: configs.failed.tooltipLabel,
          placement: configs.failed.tooltipPlacement || 'top',
        },
      },
      {
        key: 'opted_out',
        title: configs.opted_out.title,
        amount: this.unsubscribed_count,
        percentage: this.unsubscribed_percent,
        tooltipProps: {
          label: configs.opted_out.tooltipLabel,
          placement: configs.opted_out.tooltipPlacement || 'top',
        },
      },
    ]

    return filters.filter((filter) => filter.key !== 'in_progress' || !!filter.amount)
  }

  get filters() {
    return this.getFilters({ entities: this._entities })
  }
}
